import { useState, useEffect, useCallback } from 'react';

import { getAsyncTasksList, authorizeAsyncTask, downloadFile as downloadFileApi } from '../../apiClient';
import { AsyncTask, AsyncTasksTypes } from '../../utils/asyncTasks';
import { PagedState } from '../../utils/paging';
import { RefreshSignaler } from '../../utils/refresher';

/**
 * Custom hook for managing async tasks
 */
type AsyncTaskKey = keyof typeof AsyncTasksTypes;

export const useAsyncTasks = (taskType: (typeof AsyncTasksTypes)[AsyncTaskKey]) => {
    const [tasks, setTasks] = useState<PagedState<AsyncTask> | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [refresher, setRefresher] = useState<RefreshSignaler | null>(null);

    /**
     * Load async tasks list
     */
    const loadAsyncTasksList = useCallback(
        async (page: number, limit: number): Promise<RefreshSignaler> => {
            setLoading(true);
            setError(null);

            try {
                const response = await getAsyncTasksList([], page, limit, taskType);
                const mappedResponse = Object.assign(new PagedState<AsyncTask>(), response);
                mappedResponse.items = (response.items || []).map((t) => new AsyncTask(t));
                setTasks(mappedResponse);

                // Create a refresher to periodically check for updates
                const newRefresher = new RefreshSignaler(async () => {
                    try {
                        const updatedResponse = await getAsyncTasksList([], page, limit, taskType);
                        const updatedMapped = Object.assign(new PagedState<AsyncTask>(), updatedResponse);
                        updatedMapped.items = (updatedResponse.items || []).map((t) => new AsyncTask(t));
                        setTasks(updatedMapped);
                        return true;
                    } catch (err) {
                        // console.error('Error refreshing tasks:', err);
                        return false;
                    }
                }, 5000);

                setRefresher(newRefresher);
                setLoading(false);
                return newRefresher;
            } catch (err) {
                setError('Failed to load tasks');
                setLoading(false);
                throw err;
            }
        },
        [taskType]
    );

    /**
     * Authorize an async task
     */
    const authorize = useCallback(
        async (id: string): Promise<void> => {
            try {
                await authorizeAsyncTask(taskType, id);
                // Refresh the tasks list after authorization
                if (tasks) {
                    // todo -> page
                    const updatedTasks = await getAsyncTasksList([], 1, tasks.paging.limit, taskType);
                    const updatedMapped = Object.assign(new PagedState<AsyncTask>(), updatedTasks);
                    updatedMapped.items = (updatedTasks.items || []).map((t) => new AsyncTask(t));
                    setTasks(updatedMapped);
                }
            } catch (err) {
                setError('Failed to authorize task');
                throw err;
            }
        },
        [taskType, tasks]
    );

    /**
     * Download a file from an async task
     */
    const downloadFile = useCallback(
        async (id: string) => {
            try {
                const fileUrl = await downloadFileApi(taskType, id);
                const link = document.createElement('a');
                link.href = fileUrl;
                link.click();
            } catch (err) {
                setError('Failed to download file');
                throw err;
            }
        },
        [taskType]
    );

    // Clean up the refresher when the component unmounts
    useEffect(() => {
        return () => {
            if (refresher) {
                refresher.stop();
            }
        };
    }, [refresher]);

    return {
        tasks,
        loading,
        error,
        loadAsyncTasksList,
        authorize,
        downloadFile,
        cancel: async (id: string) => {
            try {
                await import('../../apiClient').then((m) => m.cancelTask(taskType, id));
                if (tasks) {
                    const updatedTasks = await getAsyncTasksList([], 1, tasks.paging.limit, taskType);
                    const updatedMapped = Object.assign(
                        new (await import('../../utils/paging')).PagedState<AsyncTask>(),
                        updatedTasks
                    );
                    updatedMapped.items = (updatedTasks.items || []).map((t) => new AsyncTask(t));
                    setTasks(updatedMapped);
                }
            } catch (err) {
                setError('Failed to cancel task');
                throw err;
            }
        },
    };
};
